import { useLazyQuery, useMutation } from "@apollo/client";
import Download from "@mui/icons-material/Download";
import Loop from "@mui/icons-material/Loop";
import Work from "@mui/icons-material/Work";
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  GridLegacy as Grid,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import { GridActionsCellItem } from "@mui/x-data-grid-premium";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { useAlerts } from "components/Alerts/AlertProvider";
import { Dialog } from "components/Dialog";
import { IssueCount } from "components/IssueCount";
import { OmnisearchDataGrid } from "components/OmnisearchDataGrid";
import { TierIIYearPicker } from "components/TierIIYearPicker";
import { currentTierIIReportingYear } from "encamp-shared/src/constants/tierii";
import { prettyPrintEnumValue } from "encamp-shared/src/utils/prettyPrintEnumValue";
import { gql } from "generated-graphql";
import {
  JobStatus,
  JobTask,
  StaffReportJobsQuery,
  StaffReportType,
  WebAutomationAction,
} from "generated-graphql/graphql";
import { OmnisearchGridColDef } from "hooks/useOmnisearchDatagrid";
import { useQueryParams } from "hooks/useQueryParams";
import { useSelectedReportingYear } from "hooks/useSelectedReportingYear";
import { useTenant } from "hooks/useTenant";
import { useCallback, useMemo, useState } from "react";
import { prettyPrintDate, prettyPrintDateTime } from "util/dates";

/**
 * Configuration for available staff report types.
 * Each entry defines the enum value and its display label.
 */
const REPORT_TYPES: { value: StaffReportType; label: string }[] = [
  { value: StaffReportType.BillingAnalysis, label: "Billing Analysis" },
  { value: StaffReportType.CancelledPayments, label: "Cancelled Payments" },
  {
    value: StaffReportType.CersReportSubmissionStatus,
    label: "CERS Report Submission Status",
  },
  { value: StaffReportType.FacilityBilling, label: "Facility Billing" },
  { value: StaffReportType.FacilityContacts, label: "Facility Contacts" },
  { value: StaffReportType.FacilityCredentials, label: "Facility Credentials" },
  {
    value: StaffReportType.FacilitiesNeedingImplementation,
    label: "Facilities Needing Implementation",
  },
  {
    value: StaffReportType.HighestGeneratorStatus,
    label: "Highest Generator Status",
  },
  { value: StaffReportType.NewUsers, label: "New Users" },
  {
    value: StaffReportType.NewlyNotReportingFacilities,
    label: "Newly Not Reporting Facilities",
  },
  { value: StaffReportType.NewlyReporting, label: "Newly Reporting" },
  {
    value: StaffReportType.NotReportingAnalysis,
    label: "Not Reporting Analysis",
  },
  { value: StaffReportType.Rbr, label: "RBR" },
  {
    value: StaffReportType.ReportsNeedingRevision,
    label: "Reports Needing Revision",
  },
  {
    value: StaffReportType.TierIiReportAssignees,
    label: "Tier II Report Assignees",
  },
  { value: StaffReportType.TierIiValidation, label: "Tier II Validation" },
  { value: StaffReportType.YtdOverThreshold, label: "YTD Over Threshold" },
];

gql(`
  fragment StaffReportJobFragment on Job {
    id
    task
    action
    status
    updatedAt
    totalIssues
    staffReportKey
    parameters
    tenant {
      id
      name
    }
    user {
      person {
        first
        last
      }
    }
  }
`);

const STAFF_REPORTS = gql(`
  query StaffReportJobs($search: String, $page: Int, $pageSize: Int, $sort: [SortModel!]) {
    jobs(search: $search, page: $page, pageSize: $pageSize, sort: $sort) {
      count
      items {
        ...StaffReportJobFragment
      }
    }
  }
`);

const GET_DOCUMENT_DOWNLOAD_LINK_QUERY = gql(`
  query GetStaffReportDownloadLink($id: ID!) {
    job(id: $id) {
      staffReportDownloadLink
      output
    }
  }
`);

const GENERATE_STAFF_REPORT = gql(`
  mutation GenerateStaffReport($reportType: StaffReportType!, $reportingYear: Int, $startDate: DateTime, $endDate: DateTime, $tenantId: String) {
    generateStaffReport(reportType: $reportType, reportingYear: $reportingYear, startDate: $startDate, endDate: $endDate, tenantId: $tenantId) {
      ...StaffReportJobFragment
    }
  }
`);

type Row = StaffReportJobsQuery["jobs"]["items"][number];

/**
 * Converts a date to midnight UTC of the same calendar date.
 * This ensures consistent date handling regardless of user timezone.
 */
const toMidnightUTC = (date: Date): Date => {
  return new Date(
    Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0)
  );
};

export const StaffReports = () => {
  const { tenantId, tenant } = useTenant();
  const alerts = useAlerts();

  const [getDownloadLink] = useLazyQuery(GET_DOCUMENT_DOWNLOAD_LINK_QUERY, {
    onCompleted: ({ job: { staffReportDownloadLink, output } }) => {
      if (!staffReportDownloadLink) {
        alerts.info(output.message ?? "No report was created.");
      } else {
        window.open(staffReportDownloadLink, "_blank");
      }
    },
    onError(error) {
      alerts.error("An error occurred while downloading the report", error);
    },
  });
  const [reportHasBeenGenerated, setReportHasBeenGenerated] = useState(false);
  const [reportType, setReportType] = useState<StaffReportType | null>(null);
  const [reportingYear, setReportingYear] = useState<number | null>(
    useSelectedReportingYear()
  );
  const [startDate, setStartDate] = useState<Date | null>(
    new Date(currentTierIIReportingYear, 0, 1)
  );
  const [endDate, setEndDate] = useState<Date | null>(new Date());

  const [usersAddedSince, setUsersAddedSince] = useState<Date | null>(null);
  const [runForAllTenants, setRunForAllTenants] = useState(false);

  const [generateReport, { loading }] = useMutation(GENERATE_STAFF_REPORT, {
    refetchQueries: [STAFF_REPORTS],
    onCompleted: () => {
      setGenerateReportOpen(false);
      setReportHasBeenGenerated(true);
    },
    onError: (error) => {
      alerts.error("Error generating report", error);
      setGenerateReportOpen(false);
    },
  });
  const [generateReportOpen, setGenerateReportOpen] = useState(false);

  const {
    filters: { ry },
    setFilters,
  } = useQueryParams<{ ry: string }>();

  const reportIsTenanted: boolean = useMemo(() => {
    if (reportType === null) {
      return true;
    }

    const nonTenantedReports = [
      StaffReportType.BillingAnalysis,
      StaffReportType.CancelledPayments,
      StaffReportType.FacilityBilling,
      StaffReportType.Rbr,
      StaffReportType.NewUsers,
      StaffReportType.TierIiReportAssignees,
    ];

    // If running certain reports for all tenants, don't tenate it
    if (
      (reportType === StaffReportType.NotReportingAnalysis ||
        reportType === StaffReportType.NewlyReporting ||
        reportType === StaffReportType.HighestGeneratorStatus ||
        reportType === StaffReportType.ReportsNeedingRevision ||
        reportType === StaffReportType.FacilitiesNeedingImplementation) &&
      runForAllTenants
    ) {
      return false;
    }

    return !nonTenantedReports.includes(reportType);
  }, [reportType, runForAllTenants]);

  const scopedByReportingYear: boolean = useMemo(
    () =>
      reportType !== null && ![StaffReportType.NewUsers].includes(reportType),
    [reportType]
  );

  const columns: OmnisearchGridColDef<Row>[] = useMemo(
    () => [
      {
        field: "tenantName",
        headerName: "Organization",
        flex: 1,
        valueGetter: (_, row) => row.tenant?.name ?? "All tenants",
      },
      {
        field: "reportType",
        headerName: "Report Type",
        flex: 1,
        valueGetter: (_, row) => {
          if (row.task === JobTask.WebAutomation) {
            return prettyPrintEnumValue(row.action);
          }

          if (row.task === JobTask.ExportData) {
            // For EXPORT_DATA jobs, display the export type from parameters
            const exportType = row.parameters["exportType"];
            return exportType
              ? `Export: ${prettyPrintEnumValue(exportType)}`
              : "Export Data";
          }

          return row.parameters["reportType"];
        },
      },
      {
        field: "status",
        headerName: "Status",
        flex: 1,
        filterKeyType: "enumAutocomplete",
        enumValues: Object.values(JobStatus),
      },
      {
        field: "updatedAt",
        headerName: "Generated On",
        valueFormatter: (_, row) => prettyPrintDateTime(row.updatedAt),
        flex: 1,
        filterKeyType: "time",
      },
      {
        field: "totalIssues",
        headerName: "Issues",
        headerAlign: "center",
        align: "center",
        renderCell: ({ value }) => <IssueCount issueCount={value} />,
        filterKeyType: "number",
      },
      {
        field: "reportingYear",
        headerName: "Reporting Year",
        flex: 1,
        valueGetter: (_, row) => row.parameters["reportingYear"],
      },
      {
        field: "generatedBy",
        headerName: "Generated By",
        flex: 1,
        valueGetter: (_, row) =>
          `${row.user?.person?.first ?? ""} ${row.user?.person?.last ?? ""}`,
      },
      {
        field: "startDate",
        headerName: "Start Date",
        flex: 1,
        valueGetter: (_, row) => row.parameters["startDate"],
        valueFormatter: (_, row) =>
          row.parameters["startDate"]
            ? prettyPrintDate(row.parameters["startDate"])
            : "",
      },
      {
        field: "endDate",
        headerName: "End Date",
        flex: 1,
        valueGetter: (_, row) => row.parameters["endDate"],
        valueFormatter: (_, row) =>
          row.parameters["endDate"]
            ? prettyPrintDate(row.parameters["endDate"])
            : "",
      },
      {
        field: "actions",
        type: "actions",
        getActions({ row }) {
          const actions = [
            <GridActionsCellItem
              key="goToJob"
              label="Go to Job"
              icon={<Work />}
              onClick={() => {
                window.open(`/staff/jobs/${row.id}`, "_blank");
              }}
            />,
          ];
          if (row.status === JobStatus.Succeeded)
            actions.push(
              <GridActionsCellItem
                key="download"
                label="Download"
                icon={<Download />}
                onClick={() =>
                  getDownloadLink({
                    variables: { id: row.id },
                  })
                }
              />
            );

          if (
            row.status === JobStatus.Queued ||
            row.status === JobStatus.Running
          )
            actions.push(
              <GridActionsCellItem
                key="queued"
                label="Queued"
                disabled
                icon={<Loop />}
              />
            );

          return actions;
        },
      },
    ],
    [getDownloadLink]
  );

  const shouldPoll = useCallback((data: StaffReportJobsQuery) => {
    const polling = data.jobs.items.some((j) =>
      [
        JobStatus.Running,
        JobStatus.Queued,
        JobStatus.ProvisioningEcsTask,
      ].includes(j.status as JobStatus)
    );
    return polling ? 2000 : undefined;
  }, []);

  const shouldDisableGenerateButton = useMemo(() => {
    if (reportType === null) {
      return true;
    }
    if (reportHasBeenGenerated) {
      return true;
    }
    return false;
  }, [reportType, reportHasBeenGenerated]);

  return (
    <>
      <Box
        sx={{
          mb: 2,
          display: "flex",
          alignItems: "center",
          flexDirection: "column",
          width: "100%",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            width: "100%",
            mb: reportType === StaffReportType.BillingAnalysis ? 2 : 0,
          }}
        >
          <FormControl sx={{ width: 275, mr: 2 }}>
            <InputLabel shrink>Report Type</InputLabel>
            <Select
              value={reportType ?? ""}
              label="Report Type"
              displayEmpty
              notched
              renderValue={(selected) => {
                if (!selected) {
                  return (
                    <Typography color="text.secondary">
                      Select a report type
                    </Typography>
                  );
                }
                return REPORT_TYPES.find((r) => r.value === selected)?.label;
              }}
              onChange={(e) => {
                setReportType(e.target.value as StaffReportType);
                setReportHasBeenGenerated(false);
                setRunForAllTenants(false);
              }}
            >
              {REPORT_TYPES.map(({ value, label }) => (
                <MenuItem key={value} value={value}>
                  {label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button
            key="generate"
            variant="contained"
            disabled={shouldDisableGenerateButton}
            onClick={() => setGenerateReportOpen(true)}
          >
            Generate Report
          </Button>
        </Box>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          {reportType !== null &&
            ![
              StaffReportType.NewUsers,
              StaffReportType.CersReportSubmissionStatus,
            ].includes(reportType) && (
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth>
                  <TierIIYearPicker
                    value={reportingYear !== null ? String(reportingYear) : ""}
                    onChange={(val) => {
                      if (val === "" || val === null || isNaN(Number(val))) {
                        setReportingYear(null);
                        setFilters((f) => {
                          f.ry = undefined;
                        });
                      } else {
                        setReportingYear(Number(val));
                        setFilters((f) => {
                          f.ry = String(val);
                        });
                      }
                    }}
                    showNoneOption={
                      reportType === StaffReportType.BillingAnalysis
                    }
                    tooltipText={
                      reportType === StaffReportType.BillingAnalysis
                        ? 'Reporting Year only applies to "Annual" reports for this report'
                        : undefined
                    }
                    selectProps={{
                      variant: "outlined",
                      size: "medium",
                      label: "Reporting Year",
                    }}
                  />
                </FormControl>
              </Grid>
            )}
          {reportType === StaffReportType.BillingAnalysis && (
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <Grid item xs={12} sm={6} md={4}>
                <DatePicker
                  label="Start Date"
                  value={startDate}
                  onChange={(date) => {
                    setStartDate(date);
                  }}
                  slotProps={{ textField: { fullWidth: true } }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <DatePicker
                  label="End Date"
                  value={endDate}
                  onChange={(date) => {
                    setEndDate(date);
                  }}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                    },
                  }}
                />
              </Grid>
            </LocalizationProvider>
          )}

          {(reportType === StaffReportType.NotReportingAnalysis ||
            reportType === StaffReportType.NewlyReporting ||
            reportType === StaffReportType.HighestGeneratorStatus ||
            reportType === StaffReportType.ReportsNeedingRevision ||
            reportType === StaffReportType.FacilitiesNeedingImplementation) && (
            <Grid
              item
              xs={12}
              sm={6}
              md={4}
              sx={{ display: "flex", alignItems: "center" }}
            >
              <FormControlLabel
                control={
                  <Checkbox
                    checked={runForAllTenants}
                    onChange={(e) => {
                      setRunForAllTenants(e.target.checked);
                      setReportHasBeenGenerated(false);
                    }}
                  />
                }
                label="Run for all tenants"
              />
            </Grid>
          )}

          {reportType === StaffReportType.NewUsers && (
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <Grid item xs={4}>
                <DatePicker
                  label="Added Since"
                  value={usersAddedSince}
                  onChange={(date) => setUsersAddedSince(date)}
                  slotProps={{ textField: { fullWidth: true } }}
                />
                <Typography pt={1} variant="body2">
                  If no date is provided, the report will return all active
                  users
                </Typography>
              </Grid>
            </LocalizationProvider>
          )}

          {reportType === StaffReportType.CersReportSubmissionStatus && (
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <Grid item xs={12} sm={6} md={4}>
                <DatePicker
                  label="Start Date"
                  value={startDate}
                  onChange={(date) => {
                    setStartDate(date);
                  }}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      helperText:
                        "Start date for HMBP submissions. Any submissions before this date will be ignored.",
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <DatePicker
                  label="End Date"
                  value={endDate}
                  onChange={(date) => {
                    setEndDate(date);
                  }}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      helperText:
                        "Latest date to check for submission statuses",
                    },
                  }}
                />
              </Grid>
            </LocalizationProvider>
          )}
        </Grid>
      </Box>
      <OmnisearchDataGrid
        persistenceKey="staff-reports"
        isURLDriven
        withPadding={false}
        columns={columns}
        dataQuery={STAFF_REPORTS}
        defaultSearch={`task:${JobTask.StaffReport} tenantId:${tenantId} or task:${JobTask.StaffReport} tenantId:null or task:${JobTask.WebAutomation} action:${WebAutomationAction.PortalSubmissionStatus} tenantId:${tenantId} or task:${JobTask.WebAutomation} action:${WebAutomationAction.PortalSubmissionStatus} tenantId:null or task:${JobTask.ExportData} staffOnly:true`}
        getItems={(data) => data.jobs.items}
        getCount={(data) => data.jobs.count}
        shouldPoll={shouldPoll}
        initialSortModel={[{ sort: "desc", field: "updatedAt" }]}
        excludeFilterColumns={["reportType", "tenantName"]}
      />
      <Dialog
        open={generateReportOpen}
        onClose={() => setGenerateReportOpen(false)}
        title="Generate Staff Report"
        primaryActionButtonProps={{
          saveText: "Generate",
          hideStartIcon: true,
          loading: loading,
          onClick: () => {
            const variables: any = {
              reportType: reportType,
              tenantId: reportIsTenanted ? tenantId : undefined,
              ...(reportType === StaffReportType.BillingAnalysis && {
                startDate: startDate ? toMidnightUTC(startDate) : null,
                endDate: endDate ? toMidnightUTC(endDate) : null,
              }),
              ...(reportType === StaffReportType.NewUsers && {
                startDate: usersAddedSince,
              }),
              ...(reportType === StaffReportType.CersReportSubmissionStatus && {
                startDate,
                endDate,
              }),
            };
            if (reportingYear !== null) {
              variables.reportingYear = reportingYear;
            }
            generateReport({ variables });
          },
        }}
      >
        This will generate a {prettyPrintEnumValue(reportType)} report. Are you
        sure you want to generate a {prettyPrintEnumValue(reportType)} report
        for {reportIsTenanted && tenant?.name ? tenant.name : "all tenants"}?
        {reportType === StaffReportType.BillingAnalysis && (
          <Box sx={{ mt: 2 }}>
            <p>
              Start Date:{" "}
              {startDate ? startDate.toLocaleDateString() : "Not specified"}
            </p>
            <p>
              End Date:{" "}
              {endDate ? endDate.toLocaleDateString() : "Not specified"}
            </p>
          </Box>
        )}
        {/* We should run all tenant validation reports on ECS as a lambda
              is likely not capable of validating all data for a reporting year */}
        {/* <Box paddingTop={theme.spacing(2)} />
          <Typography variant="subtitle2">
            You can also generate a report for all tenants by clicking{" "}
            <b
              style={{ cursor: "pointer" }}
              onClick={() =>
                generateReport({
                  variables: {
                    reportType: StaffReportType.TierIiValidation,
                    reportingYear: currentTierIIReportingYear,
                  },
                })
              }
            >
              <span style={{ textDecoration: "underline" }}>here</span>
            </b>
          </Typography> */}
      </Dialog>
    </>
  );
};
